/**
 * Servicio de Documentos Anexos por club a un evento.
 *
 * Cada club que participa en un evento puede subir N documentos (contrato de
 * transporte, voto de junta, etc.) con etiquetas para que el organizador
 * (asociación) los consulte en el panel consolidado.
 *
 * Los archivos se almacenan SIN cifrado en Storage en
 *   `events/{eventId}/club_attachments/{clubId}/...`
 * porque son documentos administrativos compartidos con el organizador (no
 * son datos personales sensibles). Las Storage Rules limitan lectura a
 * usuarios activos.
 */

import { where, Timestamp, serverTimestamp } from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { storage, auth } from "@/lib/firebase";
import { create, remove, subscribeToCollection } from "./base";
import { resizeImageIfNeeded } from "./imageResize";
import type { EventClubAttachment, EventClubAttachmentTag } from "@/types";

const COLLECTION = "eventClubAttachments";

function sortByUploadedAtDesc(
  items: EventClubAttachment[],
): EventClubAttachment[] {
  return [...items].sort((a, b) => {
    const aMs =
      a.uploadedAt instanceof Date
        ? a.uploadedAt.getTime()
        : (a.uploadedAt?.toMillis?.() ?? 0);
    const bMs =
      b.uploadedAt instanceof Date
        ? b.uploadedAt.getTime()
        : (b.uploadedAt?.toMillis?.() ?? 0);
    return bMs - aMs;
  });
}

export const ATTACHMENT_TAG_LABELS: Record<EventClubAttachmentTag, string> = {
  contrato_transporte: "Contrato de transporte",
  contrato_camion: "Contrato de camión",
  voto_junta: "Voto de junta",
  permiso_padres: "Permiso de padres",
  seguro: "Póliza / seguro",
  formato_economas: "Formato ecónomas",
  carta_junta_iglesia: "Carta aprobación Junta de Iglesia",
  otro: "Otro",
};

export const ATTACHMENT_TAG_OPTIONS: {
  value: EventClubAttachmentTag;
  label: string;
}[] = (Object.keys(ATTACHMENT_TAG_LABELS) as EventClubAttachmentTag[]).map(
  (value) => ({ value, label: ATTACHMENT_TAG_LABELS[value] }),
);

/** Suscripción en tiempo real a los adjuntos de un club para un evento */
export function subscribeToEventClubAttachments(
  eventId: string,
  clubId: string,
  callback: (items: EventClubAttachment[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<EventClubAttachment>(
    COLLECTION,
    (items) => callback(sortByUploadedAtDesc(items)),
    [where("eventId", "==", eventId), where("clubId", "==", clubId)],
    onError,
  );
}

/** Suscripción en tiempo real a TODOS los adjuntos de un evento (vista organizador) */
export function subscribeToAllEventAttachments(
  eventId: string,
  callback: (items: EventClubAttachment[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return subscribeToCollection<EventClubAttachment>(
    COLLECTION,
    (items) => callback(sortByUploadedAtDesc(items)),
    [where("eventId", "==", eventId)],
    onError,
  );
}

export interface UploadAttachmentInput {
  eventId: string;
  eventName: string;
  clubId: string;
  clubName: string;
  tag: EventClubAttachmentTag;
  customLabel?: string;
  description?: string;
  file: File;
  uploadedByName: string;
}

const ALLOWED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];
const MAX_SIZE_BYTES = 15 * 1024 * 1024; // 15MB

export async function uploadEventClubAttachment(
  input: UploadAttachmentInput,
): Promise<EventClubAttachment> {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("No autenticado");

  const { file } = input;
  if (file.size > MAX_SIZE_BYTES) {
    throw new Error(`El archivo supera el tamaño máximo (15 MB).`);
  }
  if (!ALLOWED_TYPES.includes(file.type) && file.type) {
    // permitir si type vacío (algunos navegadores lo dejan así)
    throw new Error(
      `Tipo de archivo no permitido. Use PDF, imagen o documento Word.`,
    );
  }

  // Optimización: si es imagen, redimensionamos en cliente antes de subir.
  // PDFs y documentos Word pasan intactos.
  const optimized = await resizeImageIfNeeded(file, { maxDimension: 1600 });

  const safeName = optimized.name.replace(/[^A-Za-z0-9._-]/g, "_");
  const stamp = Date.now();
  const path = `events/${input.eventId}/club_attachments/${input.clubId}/${stamp}_${safeName}`;
  const storageRef = ref(storage, path);

  await uploadBytes(storageRef, optimized, {
    contentType: optimized.type || "application/octet-stream",
    customMetadata: {
      uploaderUid: uid,
      eventId: input.eventId,
      clubId: input.clubId,
      tag: input.tag,
    },
  });
  const fileUrl = await getDownloadURL(storageRef);

  const docPayload = {
    eventId: input.eventId,
    eventName: input.eventName,
    clubId: input.clubId,
    clubName: input.clubName,
    tag: input.tag,
    ...(input.customLabel ? { customLabel: input.customLabel } : {}),
    ...(input.description ? { description: input.description } : {}),
    fileName: optimized.name,
    fileUrl,
    filePath: path,
    fileContentType: optimized.type || "application/octet-stream",
    fileSize: optimized.size,
    uploadedBy: uid,
    uploadedByName: input.uploadedByName,
    uploadedAt: serverTimestamp() as unknown as Timestamp,
  };

  const id = await create(
    COLLECTION,
    docPayload as unknown as Parameters<typeof create>[1],
    uid,
  );
  return { id, ...docPayload } as unknown as EventClubAttachment;
}

export async function deleteEventClubAttachment(
  attachment: EventClubAttachment,
): Promise<void> {
  // Borrar primero el doc (rules permiten al uploader)
  await remove(COLLECTION, attachment.id);
  // Best-effort borrar archivo en Storage (si falla, queda huérfano y lo recoge cleanup)
  try {
    await deleteObject(ref(storage, attachment.filePath));
  } catch (err) {
    console.warn(
      "[eventAttachments] No se pudo borrar archivo en Storage",
      err,
    );
  }
}
